import { MouseEvent, createContext, useContext, useEffect, useRef, useState } from 'react';
import { sleep } from '../../../utils/generics';
import {
  FilterOptionV3,
  FilterProps,
  MultiDynamicOptionsFilter,
  PossibleFilters,
} from '../../Crud/filters/sharedBetweenFilters/types';
import FilterFactory from '../factories/filter-factory';
import { CriteriaMenu, FilterStep } from '../filter.types';
import {
  FilterContextProps,
  HandleClickSelectedFilterByIndex,
  HandleSelectFilterProps,
} from './filterV3.context.types';

const FilterV3Context = createContext<FilterContextProps>({
  anchorEl: null,
  formattedFilters: [],
  options: [],
  menu: {
    step: FilterStep.FIRST_STEP,
    filterIndex: undefined,
  },
  handleClickSelectedFilter: () => undefined,
  handleClickSelectedFilterByIndex: () => undefined,
  handleBackToFirstStep: () => undefined,
  handleClosePopover: () => undefined,
  handleSelectFilter: () => undefined,
  onChangeAnchorEl: () => undefined,
  onChangeMenu: () => undefined,
});

export const useFilterV3Context = () => useContext<FilterContextProps>(FilterV3Context);

type FilterV3ContextProviderProps = {
  children: React.ReactNode;
  options: FilterOptionV3[];
};

const initialState: CriteriaMenu = {
  filterIndex: undefined,
  step: FilterStep.FIRST_STEP,
  enableReturnToList: false,
};

export const FilterV3ContextProvider = ({ children, options }: FilterV3ContextProviderProps) => {
  const isSelectedOptionsLoaded = useRef(false);

  const [menu, setMenu] = useState<CriteriaMenu>(initialState);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleSelectFilter = ({ filter, data, enableReturnToList }: HandleSelectFilterProps) => {
    // TODO: adicionar um 'key' para identificar as 'options' e os 'filters'
    const filterIndex = options.findIndex(({ title }) => title === filter.title);
    setMenu({
      step: FilterStep.SECOND_STEP,
      filterIndex: filterIndex === -1 ? undefined : filterIndex,
      data,
      enableReturnToList: enableReturnToList || false,
    });
  };

  const handleBackToFirstStep = () => {
    setMenu(initialState);
  };

  const handleClosePopover = async () => {
    setAnchorEl(null);
    await sleep(300);
    setMenu(initialState);
  };

  const handleClickSelectedFilter = (event: MouseEvent<HTMLElement>, filter: FilterProps) => {
    setAnchorEl(event.currentTarget);
    handleSelectFilter({ filter });
  };

  const handleClickSelectedFilterByIndex = ({
    event,
    filterIndex,
    data,
    enableReturnToList,
  }: HandleClickSelectedFilterByIndex) => {
    setAnchorEl(event.currentTarget);
    setMenu({
      step: FilterStep.SECOND_STEP,
      filterIndex,
      data,
      enableReturnToList,
    });
  };

  const formattedFilters = options.map((filter) => {
    const factory = new FilterFactory().getFactory(filter);
    return {
      ...filter,
      hasValue: factory.hasValue(),
    };
  });

  useEffect(() => {
    const loadSelectedOptions = async () => {
      if (isSelectedOptionsLoaded.current) {
        return;
      }

      for (const filter of formattedFilters) {
        if (filter.hasValue && filter.type === PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA) {
          const { multiDynamicOptionsArgs } = filter as MultiDynamicOptionsFilter;
          multiDynamicOptionsArgs.handleLoadSpecifOptions(multiDynamicOptionsArgs.selectedOptions);
        }
      }
    };

    loadSelectedOptions();
    isSelectedOptionsLoaded.current = true;
  }, [formattedFilters]);

  const value: FilterContextProps = {
    anchorEl,
    onChangeAnchorEl: setAnchorEl,
    menu,
    formattedFilters,
    handleClosePopover,
    handleClickSelectedFilter,
    handleBackToFirstStep,
    handleSelectFilter,
    onChangeMenu: setMenu,
    handleClickSelectedFilterByIndex,
    options,
  };

  return <FilterV3Context.Provider value={value}>{children}</FilterV3Context.Provider>;
};
